import { ReactNode, UIEvent, useEffect, useRef, useState } from 'react'
import { space, SpaceProps, LayoutProps, layout, display, DisplayProps, flexbox, FlexboxProps } from 'styled-system'
import styled from 'styled-components'
import { renderFetching } from 'utils/css'
import { BoxProps } from 'components/Box'

export type Props = SpaceProps &
  LayoutProps &
  DisplayProps &
  FlexboxProps & {
    offsetBottom?: number
    children?: ReactNode
    isScrollable?: boolean
    isFetching?: boolean
    overflow?: BoxProps['overflow']
    shouldAddEventListeners?: boolean
    onScroll?: (e: UIEvent) => void
  }

const DynamicContainer = ({ offsetBottom = 0, shouldAddEventListeners = true, children, ...containerProps }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [dynamicHeight, setDynamicHeight] = useState(0)
  const rafIdRef = useRef<number>(0)

  const recalcHeight = () => {
    cancelAnimationFrame(rafIdRef.current)
    rafIdRef.current = requestAnimationFrame(() => {
      const offsetTop = (containerRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY
      setDynamicHeight(window.innerHeight - offsetTop - offsetBottom)
    })
  }

  useEffect(() => {
    recalcHeight()

    if (shouldAddEventListeners) {
      window.addEventListener('resize', recalcHeight)
      window.addEventListener('scroll', recalcHeight, true)
    }

    return () => {
      cancelAnimationFrame(rafIdRef.current)
      if (shouldAddEventListeners) {
        window.removeEventListener('resize', recalcHeight)
        window.removeEventListener('scroll', recalcHeight, true)
      }
    }
  }, [shouldAddEventListeners, offsetBottom])

  return (
    <Container ref={containerRef} {...containerProps} height={dynamicHeight}>
      {children}
    </Container>
  )
}

const Container = styled.div<Props & { height: number }>`
  ${display}
  ${space}
  ${layout}
  ${flexbox}
  ${renderFetching}
  ${({ isScrollable = true, overflow = 'auto' }) => (isScrollable ? `overflow-y: ${overflow};` : '')}
  ${({ height }) => `height: ${height}px;`}
`

export default DynamicContainer
